// components/OverallReport.jsx
// Standalone component for the Onboarding Overall Report
// Requires: useSuperAdmin context with fetchLocations, fetchCompaniesByLocations,
//           fetchAdminData, fetchOnboardingOverallReport

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSuperAdmin } from "../../context/SuperAdminContext";
import DateRangePicker from "../Daterangepicker";

// ─── Formatters ────────────────────────────────────────────────────────────────

const formatDisplayDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatRangeDisplay = (s, e) => {
  if (!s && !e) return "";
  if (s && !e) return formatDisplayDate(s);
  if (!s && e) return formatDisplayDate(e);
  return `${formatDisplayDate(s)} – ${formatDisplayDate(e)}`;
};

// ─── Shared UI primitives ──────────────────────────────────────────────────────

const StateCard = ({ icon, title, subtitle }) => (
  <div className="bg-white border border-gray-200 rounded-xl py-20 flex flex-col items-center justify-center text-center">
    <div className="text-gray-300 mb-3">{icon}</div>
    <p className="text-gray-500 text-sm font-medium">{title}</p>
    {subtitle && <p className="text-gray-400 text-xs mt-1">{subtitle}</p>}
  </div>
);

const Spinner = ({ size = 4, color = "indigo" }) => (
  <div
    className={`animate-spin rounded-full h-${size} w-${size} border-b-2 border-${color}-600`}
  />
);

// ─── Company Breakdown Modal ───────────────────────────────────────────────────

const CompanyBreakdownModal = ({ title, companies, onClose }) => {
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!companies?.length) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — plain table */}
        <div className="overflow-y-auto max-h-72">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Company</th>
                <th className="px-5 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {companies.map((c, idx) => (
                <tr key={c.companyId || idx}>
                  <td className="px-5 py-2.5 text-sm text-gray-700">{c.companyName}</td>
                  <td className="px-5 py-2.5 text-center text-sm text-gray-700">{(c.count || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Multi-select Dropdown ─────────────────────────────────────────────────────

const MultiSelect = ({ label, options, selected, onChange, disabled, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((s) => s !== id));
    else onChange([...selected, id]);
  };

  const selectAll = () => onChange(options.map((o) => o.value));
  const clearAll = () => onChange([]);

  const displayText =
    selected.length === 0
      ? placeholder || `Select ${label}`
      : selected.length === options.length
      ? `All ${label} (${options.length})`
      : selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label || "1 selected"
      : `${selected.length} ${label} selected`;

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg transition-colors text-left
          ${disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200" : "bg-white text-gray-700 border-gray-300 hover:border-indigo-400 cursor-pointer"}
          ${open ? "border-indigo-500 ring-1 ring-indigo-500" : ""}
        `}
      >
        <span className={`truncate ${selected.length === 0 ? "text-gray-400" : ""}`}>
          {displayText}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Select / Clear all */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Select all
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">No options available</div>
            ) : (
              options.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="w-3.5 h-3.5 rounded accent-indigo-600"
                  />
                  <span className="text-sm text-gray-700 truncate">{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Summary Cards ─────────────────────────────────────────────────────────────

const CHECK_KEYS = [
  { key: "aadhaar", label: "Aadhaar", color: "blue" },
  { key: "kyc", label: "KYC", color: "emerald" },
  { key: "refCheck", label: "Ref Check", color: "amber" },
  { key: "esharm", label: "eShram", color: "purple" },
  { key: "companyCheck", label: "Company Check", color: "rose" },
  { key: "nameVerification", label: "Account Verification", color: "teal" },
];

const colorMap = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  purple: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", dot: "bg-purple-500" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200", dot: "bg-teal-500" },
};

const TotalsBar = ({ totals, companyBreakdown, onCompanyCheckClick }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
    {CHECK_KEYS.map(({ key, label, color }) => {
      const c = colorMap[color];
      const value = totals[key] || 0;
      const isCompanyCheck = key === "companyCheck";
      const isClickable = isCompanyCheck && value > 0 && companyBreakdown?.length > 0;

      return (
        <div
          key={key}
          className={`${c.bg} ${c.border} border rounded-xl px-4 py-3 flex flex-col gap-1`}
        >
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${c.dot}`} />
            <span className={`text-xs font-medium ${c.text}`}>{label}</span>
          </div>
          {isClickable ? (
            <button
              type="button"
              onClick={() => onCompanyCheckClick(companyBreakdown)}
              className="text-2xl font-bold text-indigo-600 underline text-left hover:text-indigo-800 transition-colors"
            >
              {value.toLocaleString()}
            </button>
          ) : (
            <span className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</span>
          )}
        </div>
      );
    })}
  </div>
);

// ─── Recruiter Summary Table ───────────────────────────────────────────────────

const RecruiterTable = ({ recruiterSummary, onCompanyCheckClick }) => {
  if (!recruiterSummary?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-700">By Team Member</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                Team Member
              </th>
              {CHECK_KEYS.map(({ key, label }) => (
                <th key={key} className="px-4 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {recruiterSummary.map((r) => (
              <tr key={r.adminId} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{r.adminName}</td>
                {CHECK_KEYS.map(({ key }) => {
                  const value = r[key] || 0;
                  const isCompanyCheck = key === "companyCheck";
                  const isClickable = isCompanyCheck && value > 0 && r.byCompany?.length > 0;

                  return (
                    <td key={key} className="px-4 py-3 text-center text-sm text-gray-600">
                      {isClickable ? (
                        <button
                          type="button"
                          onClick={() => onCompanyCheckClick(r.byCompany, r.adminName)}
                          className="text-indigo-600 underline hover:text-indigo-800 transition-colors text-sm"
                        >
                          {value.toLocaleString()}
                        </button>
                      ) : (
                        value.toLocaleString()
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Company Summary Table ─────────────────────────────────────────────────────

const CompanyTable = ({ companySummary }) => {
  if (!companySummary?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-700">By Company</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Company</th>
              <th className="px-4 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Company Checks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {companySummary.map((c) => (
              <tr key={c.companyId} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{c.companyName}</td>
                <td className="px-4 py-3 text-center text-sm text-gray-600">{(c.count || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Date-wise Breakdown Table ─────────────────────────────────────────────────

const DateWiseTable = ({ dateWise }) => {
  if (!dateWise?.length) return null;
  const hasData = dateWise.some((d) =>
    CHECK_KEYS.some(({ key }) => (d.totals?.[key] || 0) > 0)
  );
  if (!hasData) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-700">Daily Breakdown</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide sticky left-0 bg-gray-50">
                Date
              </th>
              {CHECK_KEYS.map(({ key, label }) => (
                <th key={key} className="px-4 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {dateWise.map((day) => {
              const anyValue = CHECK_KEYS.some(({ key }) => (day.totals?.[key] || 0) > 0);
              return (
                <tr
                  key={day.date}
                  className={`transition-colors ${anyValue ? "hover:bg-gray-50" : "opacity-40"}`}
                >
                  <td className="px-4 py-3 text-sm text-gray-600 sticky left-0 bg-white whitespace-nowrap">
                    {formatDisplayDate(day.date)}
                  </td>
                  {CHECK_KEYS.map(({ key }) => (
                    <td key={key} className="px-4 py-3 text-center text-sm text-gray-600">
                      {(day.totals?.[key] || 0).toLocaleString()}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const OverallReport = () => {
  const {
    token,
    fetchLocations,
    fetchCompaniesByLocations,
    fetchAdminData,
    fetchOnboardingOverallReport,
  } = useSuperAdmin();

  const today = new Date().toISOString().split("T")[0];
  const defaultEnd = today;
  const defaultStart = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  })();

  // ── Filter state ──
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);

  // ── Options state ──
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [admins, setAdmins] = useState([]);

  // ── Loading states ──
  const [locLoading, setLocLoading] = useState(false);
  const [compLoading, setCompLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportData, setReportData] = useState(null);

  // ── Modal state ──
  const [modal, setModal] = useState(null); // { title, companies }

  // ── Load locations on mount ──
  useEffect(() => {
    const load = async () => {
      setLocLoading(true);
      try {
        const res = await fetchLocations();
        const list = res?.locations || res?.data || [];
        setLocations(
          list.map((l) => ({ value: l._id || l.id, label: l.name || l.locationName || l._id }))
        );
      } catch (e) {
        console.error("Failed to fetch locations:", e);
      } finally {
        setLocLoading(false);
      }
    };
    if (token) load();
  }, [token, fetchLocations]);

  // ── Load admins on mount ──
  useEffect(() => {
    const load = async () => {
      setAdminLoading(true);
      try {
        const res = await fetchAdminData();
        const list = res?.admins || [];
        setAdmins(list.map((a) => ({ value: a._id, label: a.name })));
      } catch (e) {
        console.error("Failed to fetch admins:", e);
      } finally {
        setAdminLoading(false);
      }
    };
    if (token) load();
  }, [token, fetchAdminData]);

  // ── Load companies when locations change ──
  useEffect(() => {
    if (!selectedLocations.length) {
      setCompanies([]);
      setSelectedCompanies([]);
      return;
    }
    const load = async () => {
      setCompLoading(true);
      try {
        const res = await fetchCompaniesByLocations(selectedLocations);
        const list = res?.companies || res?.data || [];
        setCompanies(
          list.map((c) => ({ value: c._id || c.id, label: c.name || c.companyName || c._id }))
        );
        setSelectedCompanies((prev) => {
          const valid = new Set(list.map((c) => c._id || c.id));
          return prev.filter((id) => valid.has(id));
        });
      } catch (e) {
        console.error("Failed to fetch companies:", e);
        setCompanies([]);
      } finally {
        setCompLoading(false);
      }
    };
    load();
  }, [selectedLocations, fetchCompaniesByLocations]);

  // ── Fetch report ──
  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const payload = {
        fromDate: startDate,
        toDate: endDate,
        ...(selectedCompanies.length ? { companyIds: selectedCompanies } : {}),
        ...(selectedAdmins.length ? { adminIds: selectedAdmins } : {}),
      };
      const res = await fetchOnboardingOverallReport(payload);
      if (!res?.success) throw new Error("Invalid response from server");
      setReportData(res.data);
    } catch (e) {
      console.error("Failed to fetch overall report:", e);
      setReportError(e.message || "Failed to load report");
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  }, [startDate, endDate, selectedCompanies, selectedAdmins, fetchOnboardingOverallReport]);

  const handleApply = () => fetchReport();

  const handleDateRangeChange = ({ startDate: s, endDate: e }) => {
    setStartDate(s);
    setEndDate(e);
  };

  // ── Modal handlers ──
  const openModal = useCallback((companies, recruiterName = null) => {
    const title = recruiterName
      ? `Company Checks — ${recruiterName}`
      : "Company Check Breakdown";
    setModal({ title, companies });
  }, []);

  const closeModal = useCallback(() => setModal(null), []);

  // ── Derived ──
  const totalsAllZero =
    reportData?.totals &&
    CHECK_KEYS.every(({ key }) => (reportData.totals[key] || 0) === 0);

  const canApply = !!(startDate && endDate);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Filters card ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Team Performance Report</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Filter by date, location, company and recruiter to generate the report
            </p>
          </div>
          {reportData && (
            <span className="text-xs text-gray-400">
              {formatRangeDisplay(reportData.fromDate, reportData.toDate)}
            </span>
          )}
        </div>

        {/* Row 1 — date picker */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Date Range</label>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              maxDate={today}
              placeholder="Select date range"
            />
          </div>

          {/* Location multi-select */}
          <div>
            <MultiSelect
              label="Locations"
              options={locations}
              selected={selectedLocations}
              onChange={setSelectedLocations}
              disabled={locLoading}
              placeholder={locLoading ? "Loading locations…" : "All locations"}
            />
          </div>

          {/* Company multi-select */}
          <div>
            <MultiSelect
              label="Companies"
              options={companies}
              selected={selectedCompanies}
              onChange={setSelectedCompanies}
              disabled={!selectedLocations.length || compLoading}
              placeholder={
                !selectedLocations.length
                  ? "Select a location first"
                  : compLoading
                  ? "Loading companies…"
                  : "All companies"
              }
            />
          </div>

          {/* Admin multi-select */}
          <div>
            <MultiSelect
              label="Team Members"
              options={admins}
              selected={selectedAdmins}
              onChange={setSelectedAdmins}
              disabled={adminLoading}
              placeholder={adminLoading ? "Loading team members.." : "All team members"}
            />
          </div>
        </div>

        {/* Apply button */}
        <div className="flex justify-end">
          <button
            onClick={handleApply}
            disabled={!canApply || reportLoading}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {reportLoading ? (
              <>
                <Spinner size={4} color="white" />
                <span>Generating…</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <span>Generate Report</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── States ── */}
      {reportLoading && (
        <StateCard
          icon={<Spinner size={8} />}
          title="Generating report…"
          subtitle="Fetching onboarding data for the selected filters"
        />
      )}

      {reportError && !reportLoading && (
        <div className="bg-red-50 border border-red-200 rounded-xl py-12 flex flex-col items-center justify-center text-center">
          <svg className="w-10 h-10 text-red-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-600 text-sm font-medium">Failed to load report</p>
          <p className="text-red-400 text-xs mt-1">{reportError}</p>
          <button
            onClick={fetchReport}
            className="mt-4 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {!reportLoading && !reportError && !reportData && (
        <StateCard
          icon={
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          title="Set your filters and click Generate Report"
          subtitle="Aadhaar, KYC, Ref Check, eShram and Company Check counts will appear here"
        />
      )}

      {!reportLoading && !reportError && reportData && totalsAllZero && (
        <StateCard
          icon={
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          title="No activity found"
          subtitle="No onboarding checks were recorded for the selected filters"
        />
      )}

      {/* ── Report content ── */}
      {!reportLoading && !reportError && reportData && !totalsAllZero && (
        <div className="space-y-4">
          {/* Totals */}
          <TotalsBar
            totals={reportData.totals}
            companyBreakdown={reportData.companyBreakdown}
            onCompanyCheckClick={(companies) => openModal(companies)}
          />

          {/* Recruiter summary */}
          <RecruiterTable
            recruiterSummary={reportData.recruiterSummary}
            onCompanyCheckClick={openModal}
          />

          {/* Company summary */}
          <CompanyTable companySummary={reportData.companySummary} />

          {/* Date-wise breakdown */}
          <DateWiseTable dateWise={reportData.dateWise} />
        </div>
      )}

      {/* ── Company Breakdown Modal ── */}
      {modal && (
        <CompanyBreakdownModal
          title={modal.title}
          companies={modal.companies}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default OverallReport;